/**
 * Cookie consent store (framework-free, so scripts outside React can gate on `hasConsent`).
 *
 * Rules (Lei n.º 41/2004 / RGPD, client cookie policy section 5):
 * - essential is always on; analytics and marketing are off until the user opts in;
 * - the choice is stored with its date, the text version and the categories;
 * - it is valid for 12 months (or until the text version changes), after which it is asked again.
 */
export type ConsentCategory = 'essential' | 'analytics' | 'marketing';

export type ConsentCategories = Record<ConsentCategory, boolean>;

export interface StoredConsent {
  /** Version of the banner/policy text the user saw. */
  version: string;
  /** ISO date-time of the choice. */
  date: string;
  categories: ConsentCategories;
}

export const COOKIE_CONSENT_STORAGE_KEY = 'peoplerock_cookie_consent';
export const COOKIE_CONSENT_VERSION = 'v1.0';
export const COOKIE_CONSENT_VALIDITY_MONTHS = 12;

export interface CookieConsentState {
  /** The valid stored choice, or null when none was made / it expired / the text changed. */
  consent: StoredConsent | null;
  /** True when the settings were explicitly reopened (footer link). */
  settingsRequested: boolean;
}

export const NO_OPTIONAL_CONSENT: ConsentCategories = {
  essential: true,
  analytics: false,
  marketing: false,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** True once more than 12 months have passed since `date`. */
export function isConsentExpired(date: string, now: Date = new Date()): boolean {
  const given = new Date(date);
  if (Number.isNaN(given.getTime())) return true;
  const expiry = new Date(given);
  expiry.setMonth(expiry.getMonth() + COOKIE_CONSENT_VALIDITY_MONTHS);
  return expiry.getTime() <= now.getTime();
}

function readStoredConsent(now: Date = new Date()): StoredConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || !isRecord(parsed.categories)) return null;
    const { version, date, categories } = parsed as {
      version?: unknown;
      date?: unknown;
      categories: Record<string, unknown>;
    };
    if (version !== COOKIE_CONSENT_VERSION || typeof date !== 'string') return null;
    if (isConsentExpired(date, now)) return null;
    return {
      version,
      date,
      categories: {
        essential: true,
        analytics: categories.analytics === true,
        marketing: categories.marketing === true,
      },
    };
  } catch {
    return null;
  }
}

let state: CookieConsentState = { consent: readStoredConsent(), settingsRequested: false };
const listeners = new Set<() => void>();

function setState(next: CookieConsentState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

export const subscribeCookieConsent = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getCookieConsentState = (): CookieConsentState => state;

/** Re-reads the storage (used after the stored value changed outside this module, and by tests). */
export function refreshCookieConsent(): void {
  setState({ consent: readStoredConsent(), settingsRequested: false });
}

/** Persists a choice. Essential stays on whatever is passed. */
export function saveCookieConsent(choice: Partial<ConsentCategories>): StoredConsent {
  const consent: StoredConsent = {
    version: COOKIE_CONSENT_VERSION,
    date: new Date().toISOString(),
    categories: {
      essential: true,
      analytics: choice.analytics === true,
      marketing: choice.marketing === true,
    },
  };
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Storage unavailable: the choice still holds for this page view.
  }
  setState({ consent, settingsRequested: false });
  return consent;
}

export const acceptAllCookies = (): StoredConsent =>
  saveCookieConsent({ analytics: true, marketing: true });

export const rejectNonEssentialCookies = (): StoredConsent => saveCookieConsent({});

/** Dismisses the reopened settings dialog without changing the stored choice. */
export function closeCookieSettings(): void {
  if (state.settingsRequested) setState({ ...state, settingsRequested: false });
}

/** Reopens the banner in its settings view (the "Cookie settings" footer link). */
export function openCookieSettings(): void {
  setState({ ...state, settingsRequested: true });
}

/**
 * Gate for scripts that need consent: `if (hasConsent('analytics')) loadAnalytics()`.
 * Essential is always allowed; anything else only after an explicit, still valid opt-in.
 */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'essential') return true;
  return state.consent?.categories[category] === true;
}
